import {
	ReactPlugin,
	withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import { lazy, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
	setOnUnauthorizedHandler,
	setupApiAxiosInterceptors,
} from '@gov-nx/api';
import { ErrorBoundary, ErrorPage } from '@gov-nx/component/web';
import { rememberCurrentLocation } from '@gov-nx/utils/web';
import {
	AppContextProvider,
	ExtraConfigProvider,
	getQueryClient,
	InitProvider,
	useAppContext,
} from '@gov-nx/core/app';
import { preparePageStructure } from '@gov-nx/module/page';
import { extraConfig } from './extra-config/extraConfig';
import { DarkmodeHelper } from './helpers/DarkmodeHelper';
import { appLoaderControl } from './helpers/loader';
import { PageLoader } from './components/PageLoader';

preparePageStructure();

const AppRoutes = lazy(() =>
	import('./routes/AppRoutes').then(({ AppRoutes }) => ({ default: AppRoutes }))
);

const isLocalhost = window.location.hostname.includes('localhost');
const environment = 'ace';
const finalEnvironment = environment.includes('NX')
	? isLocalhost
		? 'lcl'
		: 'prod'
	: environment;

const queryClient = getQueryClient();

const instrumentationKey = '{{NX_INSTRUMENTATION_KEY}}';
const finalInstrumentationKey = instrumentationKey.includes('NX');

const reactPlugin = new ReactPlugin();
if (!finalInstrumentationKey) {
	const browserHistory = createBrowserHistory();
	const appInsights = new ApplicationInsights({
		config: {
			instrumentationKey: instrumentationKey,
			extensions: [reactPlugin],
			disableCookiesUsage: true,
			extensionConfig: {
				[reactPlugin.identifier]: { history: browserHistory },
			},
		},
	});
	appInsights.loadAppInsights();
}

export const App = () => {
	const { appError } = useAppContext();

	if (appError && isLocalhost) {
		// eslint-disable-next-line no-console
		console.error(
			'APP BREAKING ERROR! \nThis would block the user from doing anything if we were not on localhost!\n',
			appError
		);
	} else if (appError) {
		appLoaderControl.hide();
		return (
			<Suspense>
				<BrowserRouter>
					<ExtraConfigProvider extraConfig={extraConfig(finalEnvironment)}>
						<ErrorPage message={appError.message} />
					</ExtraConfigProvider>
				</BrowserRouter>
			</Suspense>
		);
	}

	return (
		<ExtraConfigProvider extraConfig={extraConfig(finalEnvironment)}>
			<ErrorBoundary>
				<InitProvider>
					<Suspense fallback={<PageLoader />}>
						<AppRoutes />
					</Suspense>
				</InitProvider>
			</ErrorBoundary>
		</ExtraConfigProvider>
	);
};

export const AppWrapper = () => (
	<AppContextProvider loaderControl={appLoaderControl}>
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>
		<DarkmodeHelper />
	</AppContextProvider>
);

setupApiAxiosInterceptors();
setOnUnauthorizedHandler(rememberCurrentLocation);

export default withAITracking(reactPlugin, AppWrapper);
